// First-edit target highlight for the onboarding tour. Registered
// statically in SourcePane's BASE_EXTENSIONS (it renders nothing until
// armed) and toggled by dispatching `setFirstEditTarget(view, active)`
// — the same effect-driven pattern as changeFlashExtension, so the
// memoized SourcePane never rebuilds its extension list.
//
// When armed, the field scans the buffer for a safe numeric target,
// wraps it in a phosphor-pulsing mark (`.cm-first-edit-target`, pure
// CSS with a reduced-motion end-state in style.css) and drops a
// "change this number" hint widget after it. The decoration maps
// through edits, so the pulse follows the number while the user types
// into it; the tour disarms it once the edit has been heard.

import { StateEffect, StateField } from '@codemirror/state';
import {
  Decoration,
  EditorView,
  WidgetType,
  type DecorationSet,
} from '@codemirror/view';

export type EditTarget = { from: number; to: number };

// Preferred target: the second argument of the first `.range(lo, hi)`
// call — in DEMO_CODE that is the 1800 ceiling of the bass filter
// sweep, the most audible single number in the pattern.
const RANGE_SECOND_ARG = /\.range\(\s*-?\d+(?:\.\d+)?\s*,\s*(\d+(?:\.\d+)?)/;

// Fallback: an integer/decimal literal that isn't glued to an
// identifier (RolandTR909) or a leading dot (.5).
const BARE_NUMBER = /(?<![\w.])\d+(?:\.\d+)?/;

/**
 * Find a safe numeric literal for the guided first edit. Comment-only
 * lines are skipped so the hint never points at prose. Returns null
 * when the buffer has no candidate (the tour then runs without the
 * pulse — the coachmark copy still works).
 */
export function findFirstEditTarget(code: string): EditTarget | null {
  const preferred = RANGE_SECOND_ARG.exec(code);
  if (preferred) {
    const arg = preferred[1]!;
    // The capture group ends the match, so its offset is derivable
    // without a second scan.
    const from = preferred.index + preferred[0].length - arg.length;
    return { from, to: from + arg.length };
  }

  let offset = 0;
  for (const line of code.split('\n')) {
    const commentAt = line.indexOf('//');
    const searchable = commentAt === -1 ? line : line.slice(0, commentAt);
    const m = BARE_NUMBER.exec(searchable);
    if (m) {
      const from = offset + m.index;
      return { from, to: from + m[0].length };
    }
    offset += line.length + 1;
  }
  return null;
}

const setActiveEffect = StateEffect.define<boolean>();

class HintWidget extends WidgetType {
  toDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = 'cm-first-edit-hint';
    span.setAttribute('aria-hidden', 'true');
    span.textContent = '← change this number';
    return span;
  }
  override eq(): boolean {
    return true;
  }
}

const targetMark = Decoration.mark({ class: 'cm-first-edit-target' });
const hintWidget = Decoration.widget({ widget: new HintWidget(), side: 1 });

const firstEditTargetField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    let next = decorations.map(tr.changes);
    for (const effect of tr.effects) {
      if (!effect.is(setActiveEffect)) continue;
      if (!effect.value) {
        next = Decoration.none;
        continue;
      }
      const target = findFirstEditTarget(tr.state.doc.toString());
      next =
        target === null
          ? Decoration.none
          : Decoration.set([
              targetMark.range(target.from, target.to),
              hintWidget.range(target.to),
            ]);
    }
    return next;
  },
  provide: (f) => EditorView.decorations.from(f),
});

/** Register on a CodeMirror editor to enable the tour highlight. */
export const firstEditTargetExtension = [firstEditTargetField];

/** Arm / disarm the highlight. Safe against a destroyed view — the
 *  tour outlives view remounts (home ↔ editor swaps). */
export function setFirstEditTarget(view: EditorView, active: boolean): void {
  try {
    view.dispatch({ effects: setActiveEffect.of(active) });
  } catch {
    // View destroyed between capture and dispatch — fine; a fresh
    // view gets re-armed by the tour's effect on remount.
  }
}
